import { useEffect, useState } from 'react';
import type { TourStep } from '@/types/product-tour';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { X, ChevronLeft, ChevronRight, CheckCircle2 } from 'lucide-react';

interface TourTooltipProps {
  step: TourStep;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
  onSkip: () => void;
  onClose: () => void;
}

export function TourTooltip({
  step,
  currentStep,
  totalSteps,
  onNext,
  onPrevious,
  onSkip,
  onClose,
}: TourTooltipProps) {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [tooltipRef, setTooltipRef] = useState<HTMLDivElement | null>(null);
  const [arrowPosition, setArrowPosition] = useState<'top' | 'bottom' | 'left' | 'right' | null>(null);

  useEffect(() => {
    if (!step.target || step.placement === 'center' || !tooltipRef) {
      // True centre using transform — position at 50%/50% and let CSS translate handle it
      setPosition({
        top: -1,
        left: -1,
      });
      return;
    }

    const updatePosition = () => {
      const element = document.querySelector(step.target!);
      if (!element || !tooltipRef) return;

      const targetRect = element.getBoundingClientRect();
      const tooltipRect = tooltipRef.getBoundingClientRect();
      const padding = 20; // Increased padding for better separation
      const viewportPadding = 16;

      let top = 0;
      let left = 0;
      let preferredPlacement = step.placement || 'bottom';

      // Calculate positions for all placements
      const positions = {
        top: {
          top: targetRect.top - tooltipRect.height - padding,
          left: targetRect.left + targetRect.width / 2 - tooltipRect.width / 2,
        },
        bottom: {
          top: targetRect.bottom + padding,
          left: targetRect.left + targetRect.width / 2 - tooltipRect.width / 2,
        },
        left: {
          top: targetRect.top + targetRect.height / 2 - tooltipRect.height / 2,
          left: targetRect.left - tooltipRect.width - padding,
        },
        right: {
          top: targetRect.top + targetRect.height / 2 - tooltipRect.height / 2,
          left: targetRect.right + padding,
        },
      };

      // Check if preferred placement fits in viewport
      const checkFitsInViewport = (pos: { top: number; left: number }) => {
        return (
          pos.top >= viewportPadding &&
          pos.top + tooltipRect.height <= window.innerHeight - viewportPadding &&
          pos.left >= viewportPadding &&
          pos.left + tooltipRect.width <= window.innerWidth - viewportPadding
        );
      };

      // Try preferred placement first
      let actualPlacement: 'top' | 'bottom' | 'left' | 'right' | 'center' = preferredPlacement;
      
      if (preferredPlacement !== 'center' && positions[preferredPlacement]) {
        const preferredPos = positions[preferredPlacement];
        if (checkFitsInViewport(preferredPos)) {
          top = preferredPos.top;
          left = preferredPos.left;
          actualPlacement = preferredPlacement;
        } else {
          // Try alternative placements in order of preference
          const alternatives: Array<'top' | 'bottom' | 'left' | 'right'> = 
            preferredPlacement === 'top' || preferredPlacement === 'bottom'
              ? ['bottom', 'top', 'right', 'left']
              : ['right', 'left', 'bottom', 'top'];

          let positioned = false;
          for (const alt of alternatives) {
            const altPos = positions[alt];
            if (checkFitsInViewport(altPos)) {
              top = altPos.top;
              left = altPos.left;
              actualPlacement = alt;
              positioned = true;
              break;
            }
          }

          // If no placement fits, use preferred and constrain to viewport
          if (!positioned) {
            top = preferredPos.top;
            left = preferredPos.left;
            actualPlacement = preferredPlacement;
          }
        }
      } else {
        // Center placement
        top = window.innerHeight / 2 - tooltipRect.height / 2;
        left = window.innerWidth / 2 - tooltipRect.width / 2;
        actualPlacement = 'center';
      }

      // Final viewport constraints (but don't overlap the target)
      top = Math.max(viewportPadding, Math.min(top, window.innerHeight - tooltipRect.height - viewportPadding));
      left = Math.max(viewportPadding, Math.min(left, window.innerWidth - tooltipRect.width - viewportPadding));

      // Ensure tooltip doesn't overlap target element
      const tooltipBottom = top + tooltipRect.height;
      const tooltipRight = left + tooltipRect.width;

      // Check for overlap and adjust if necessary
      const hasOverlap = !(
        tooltipBottom < targetRect.top ||
        top > targetRect.bottom ||
        tooltipRight < targetRect.left ||
        left > targetRect.right
      );

      if (hasOverlap) {
        // Push tooltip away from target based on which side has more space
        const spaceAbove = targetRect.top;
        const spaceBelow = window.innerHeight - targetRect.bottom;
        const spaceLeft = targetRect.left;
        const spaceRight = window.innerWidth - targetRect.right;

        const maxSpace = Math.max(spaceAbove, spaceBelow, spaceLeft, spaceRight);

        if (maxSpace === spaceBelow) {
          top = targetRect.bottom + padding;
        } else if (maxSpace === spaceAbove) {
          top = targetRect.top - tooltipRect.height - padding;
        } else if (maxSpace === spaceRight) {
          left = targetRect.right + padding;
        } else {
          left = targetRect.left - tooltipRect.width - padding;
        }

        // Re-constrain to viewport
        top = Math.max(viewportPadding, Math.min(top, window.innerHeight - tooltipRect.height - viewportPadding));
        left = Math.max(viewportPadding, Math.min(left, window.innerWidth - tooltipRect.width - viewportPadding));
      }

      setPosition({ top, left });
    };

    updatePosition();

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [step.target, step.placement, tooltipRef]);

  const progress = ((currentStep + 1) / totalSteps) * 100;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  return (
    <Card
      ref={setTooltipRef}
      data-tour-tooltip
      className={`fixed w-[400px] max-w-[90vw] shadow-2xl border-2 animate-in fade-in zoom-in-95 duration-200 bg-card ${
        isLastStep ? 'border-primary/60' : ''
      }`}
      style={
        position.top === -1
          ? {
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10001,
            }
          : {
              top: position.top,
              left: position.left,
              zIndex: 10001,
            }
      }
    >
      <div className="p-6 space-y-4">
        {/* Celebration header on last step */}
        {isLastStep && (
          <div className="flex flex-col items-center text-center gap-2 pb-2 border-b border-border">
            <div className="h-12 w-12 rounded-full bg-primary/15 flex items-center justify-center">
              <CheckCircle2 className="h-6 w-6 text-primary" />
            </div>
            <p className="text-sm font-medium text-primary">Tour Complete</p>
          </div>
        )}

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold leading-tight">{step.title}</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Step {currentStep + 1} of {totalSteps}
            </p>
          </div>
          {step.showSkip !== false && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Progress bar */}
        <Progress value={progress} className="h-1" />

        {/* Description */}
        <p className="text-sm leading-relaxed">{step.description}</p>

        {/* Action button (if provided) */}
        {step.action && (
          <Button
            onClick={step.action.onClick}
            className="w-full"
            variant="outline"
          >
            {step.action.label}
          </Button>
        )}

        {/* Navigation buttons */}
        <div className="flex items-center justify-between gap-2 pt-2">
          <div className="flex gap-2">
            {step.showPrevious !== false && !isFirstStep && (
              <Button
                variant="outline"
                size="sm"
                onClick={onPrevious}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
            )}
          </div>

          <div className="flex gap-2">
            {step.showSkip !== false && !isLastStep && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onSkip}
              >
                Skip Tour
              </Button>
            )}
            <Button
              size="sm"
              onClick={onNext}
            >
              {isLastStep ? 'Finish' : 'Next'}
              {!isLastStep && <ChevronRight className="h-4 w-4 ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
